import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import ProfilePicture from '../ProfilePicture/ProfilePicture';
import UnlockedAchievements from './UnlockedAchievements';
import Badge from '../Badge/Badge';
import Tooltip from '../Tooltip/Tooltip';
import BadgeTooltip from '../BadgeTooltip/BadgeTooltip';
import userService from '../../services/userService';
import SEOHead from '../SEOHead/SEOHead';
import './Profile.scss';

const PublicProfile = () => {
  const { username } = useParams();
  const { user: currentUser } = useAuth();
  const [publicProfileData, setPublicProfileData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchFullProfile = async () => {
      if (!username) return;

      try {
        setIsLoading(true);
        setError(null);
        const fullProfile = await userService.getFullPublicProfile(username);
        setPublicProfileData(fullProfile);
      } catch (err) {
        console.error('Error fetching public profile:', err);
        setError(err.message.includes('404') ? 'User not found' : 'Failed to load profile');
      } finally {
        setIsLoading(false);
      }
    };

    fetchFullProfile();
  }, [username]);

  // Allow users to view their own public profile
  // No redirect needed - users should be able to see how their profile appears to others

  const getPublicProfileImageUrl = () => {
    if (publicProfileData?.profile_picture) {
      return `/api/profile-picture/${publicProfileData.profile_picture}`;
    }
    return '/unset-profile-pic.png';
  };

  if (isLoading) {
    return (
      <>
        <SEOHead
          title={`${username}'s Profile`}
          description={`View ${username}'s profile on GuessNexus. See their achievements, stats, and progress across daily challenge games.`}
          canonicalUrl={`https://guessnexus.com/profile/${username}`}
        />
        <div className="profile-page">
          <div className="profile-page__container">
            <div className="loading">Loading profile...</div>
          </div>
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <SEOHead
          title={error === 'User not found' ? 'User Not Found' : 'Profile Error'}
          description={error === 'User not found' ? `The user "${username}" could not be found on GuessNexus.` : 'There was an error loading this profile. Please try again later.'}
          canonicalUrl={`https://guessnexus.com/profile/${username}`}
        />
        <div className="profile-page">
          <div className="profile-page__container">
            <div className="profile-page__error">
              <h2>{error}</h2>
              <p>
                {error === 'User not found' 
                  ? `The user "${username}" could not be found.`
                  : 'There was an error loading this profile. Please try again later.'
                }
              </p>
            </div>
          </div>
        </div>
      </>
    );
  }

  if (!publicProfileData) {
    return (
      <>
        <SEOHead
          title="Profile Not Found"
          description={`The profile for "${username}" could not be loaded on GuessNexus.`}
          canonicalUrl={`https://guessnexus.com/profile/${username}`}
        />
        <div className="profile-page">
          <div className="profile-page__container">
            <div className="profile-page__error">
              <h2>Profile not found</h2>
              <p>The requested profile could not be loaded.</p>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Create structured data for the profile page
  const profileStructuredData = {
    "@context": "https://schema.org",
    "@type": "ProfilePage",
    "mainEntity": {
      "@type": "Person",
      "name": publicProfileData.username,
      "identifier": publicProfileData.username,
      "image": publicProfileData.profile_picture ? `https://guessnexus.com/api/profile-picture/${publicProfileData.profile_picture}` : "https://guessnexus.com/unset-profile-pic.png",
      "memberOf": {
        "@type": "Organization",
        "name": "GuessNexus",
        "url": "https://guessnexus.com"
      }
    },
    "isPartOf": {
      "@type": "WebSite",
      "name": "GuessNexus",
      "url": "https://guessnexus.com"
    }
  };

  return (
    <>
      <SEOHead
        title={`${publicProfileData.username}'s Profile`}
        description={`🎯 Discover ${publicProfileData.username}'s gaming journey on GuessNexus! Check out their impressive achievements, daily challenge streaks, and leaderboard stats. ${publicProfileData.total_achievements || 0} achievements unlocked and counting!`}
        canonicalUrl={`https://guessnexus.com/profile/${username}`}
        structuredData={profileStructuredData}
      />
      <div className="profile-page">
      <div className="profile-page__container">
        <div className="profile-page__header">
          <div className="profile-page__left-column">
            <div className="profile-picture profile-picture--large">
              <div className="profile-picture__container">
                <img
                  src={getPublicProfileImageUrl()}
                  alt={`${publicProfileData.username}'s profile`}
                  className="profile-picture__image"
                  onError={(e) => {
                    e.target.src = '/unset-profile-pic.png';
                  }}
                />
              </div>
            </div>
            {publicProfileData.badge && publicProfileData.badge.name && (
              <Tooltip
                content={<BadgeTooltip achievement={publicProfileData.badge} />}
                position="right"
                delay={300}
                maxWidth="320px"
              >
                <Badge 
                  title={publicProfileData.badge.name}
                  achievement={publicProfileData.badge}
                  isSelectable={false}
                />
              </Tooltip>
            )}
            <p className="profile-page__joined">
              Member since {new Date(publicProfileData.created_at).toLocaleDateString()}
            </p>
          </div>

          <div className="profile-page__right-column">
            <h1 className="profile-page__username">{publicProfileData.username}</h1>
            <div className="profile-page__bio-section">
              <h3 className="profile-page__bio-title">Bio:</h3>
              <div className="profile-page__bio-display">
                <p className="profile-page__bio-text">
                  {publicProfileData.bio || 'No bio.'}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="profile-page__stats">
          <h2>Game Statistics</h2>
          {publicProfileData?.summary ? (
            <div className="profile-page__stats-grid">
              <div className="profile-page__stat-card">
                <div className="profile-page__stat-number">{publicProfileData.summary.total_games || 0}</div>
                <div className="profile-page__stat-label">Total Games</div>
              </div>
              <div className="profile-page__stat-card">
                <div className="profile-page__stat-number">{publicProfileData.summary.total_daily_games || 0}</div>
                <div className="profile-page__stat-label">Daily Games</div>
              </div>
              <div className="profile-page__stat-card">
                <div className="profile-page__stat-number">{publicProfileData.summary.overall_best_streak || 0}</div>
                <div className="profile-page__stat-label">Best Streak</div>
              </div>
            </div>
          ) : (
            <div className="profile-page__stats-placeholder">
              <p>No statistics available</p>
            </div>
          )}
        </div>

        <UnlockedAchievements achievements={publicProfileData?.achievements} isPublicView={true} username={publicProfileData?.username} />
      </div>
    </div>
    </>
  );
};

export default PublicProfile;